/**
 * @author Youssef Tarek
 * @date 2023-02-07
 * @description segment creator for a group of filters.
 * @filename create-segment.tsx
 */

import SectionLoader from "common/section-loader";
import { SHIPPING_LOCATIONS_KEY } from "consts/advanced-filters";
import { ANALYTICS_CONTEXT } from "contexts/analytics-context";
import isEqual from "lodash.isequal";
import {
  SearchDropdownOption,
  SegmentFilters,
} from "interfaces/advanced-filters";
import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import Select, { StylesConfig, ThemeConfig } from "react-select";
import BackArrowIcon from "static/images/left-arrow.svg";
import {
  createNewSegment,
  flattenFilters,
  mapFlattenedToMultiSelectValue,
  mapMultiSelectToFlattenedValue,
  updateFiltersSegment,
} from "utilities/advanced-filters";
import { toast } from "react-toastify";
import DropdownIndicator from "./dropdown-indicator";
import MenuList from "./menu-list";

interface CreateSegmentProps {
  appliedFilters: Record<string, string[]>;
  toggleCreateANewSegment: () => void;
  segment: SegmentFilters;
}

interface CreateSegmentState {
  segmentName: string;
  flattenedFilters: Record<string, string[]>;
  selectedFilters: Record<string, string[]>;
  id: string;
}

const customTheme: ThemeConfig = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: "rgba(69, 194, 255, 1)",
    primary25: "rgba(69, 194, 255, 0.1)",
  },
});

const customStyles: StylesConfig<SearchDropdownOption> = {
  control(provided) {
    const backgroundColor = "hsla(0, 0%, 0%, 0.03)";
    return {
      ...provided,
      borderRadius: 10,
      backgroundColor,
    };
  },
  multiValue(provided) {
    return {
      ...provided,
      padding: "5px 10px",
      borderRadius: "50000px",
      backgroundColor: "hsla(217, 90%, 61%, 0.1)",
      gap: 10,
    };
  },
  multiValueRemove(provided) {
    return {
      ...provided,
      borderRadius: "50%",
      backgroundColor: "black",
      color: "white",
    };
  },
};

class CreateSegment extends React.Component<
  WithTranslation & CreateSegmentProps,
  CreateSegmentState
> {
  declare context: React.ContextType<typeof ANALYTICS_CONTEXT>;

  constructor(props) {
    super(props);
    this.state = {
      segmentName: this.props.segment?.title || "",
      flattenedFilters: {},
      selectedFilters: {},
      id: Date.now().toString(),
    };
  }

  componentDidMount(): void {
    this.updateFiltersState();
    this.context.addUpdatesListener(this.state.id, this.updateFiltersState);
    console.log("hi");
  }

  componentWillUnmount(): void {
    this.context.removeUpdatesListener(this.state.id);
  }

  getIsAllSelected(key: string) {
    return isEqual(
      this.state.selectedFilters[key],
      this.state.flattenedFilters[key]
    );
  }

  updateFiltersState() {
    this.setState({
      flattenedFilters: flattenFilters(this.context.availableFilters),
      selectedFilters: flattenFilters(
        this.props.segment === this.context.appliedFiltersSegment
          ? this.context.appliedFilters
          : this.props.segment?.filters
      ),
    });
  }

  changeHandler(key: string, value: string) {
    if (this.state.selectedFilters[key]?.includes(value)) {
      this.setState({
        selectedFilters: {
          ...this.state.selectedFilters,
          [key]: this.state.selectedFilters[key].filter(
            (storedValue) => storedValue !== value
          ),
        },
      });
    } else {
      this.setState({
        selectedFilters: {
          ...this.state.selectedFilters,
          [key]: (this.state.selectedFilters[key] || []).concat(value),
        },
      });
    }
  }

  selectAll(key: string) {
    if (this.getIsAllSelected(key)) {
      this.setState({
        selectedFilters: {
          ...this.state.selectedFilters,
          [key]: [],
        },
      });
    } else {
      this.setState({
        selectedFilters: {
          ...this.state.selectedFilters,
          [key]: this.state.flattenedFilters[key],
        },
      });
    }
  }

  createNewSegmentHandler() {
    createNewSegment({
      title: this.state.segmentName,
      filtersArg: this.state.selectedFilters,
      project: this.context.project,
    })
      .then((segment) => {
        this.props.toggleCreateANewSegment();
        this.context.updateAppliedFiltersSegment(segment);
        toast.success("Segment created successfully", {
          position: "top-left",
        });
        this.context.updateAppliedFilters(this.state.selectedFilters);
        // add the newly created segment to the list of available segments
        this.context.updateFiltersSegments([
          ...this.context.availableFiltersSegments,
          segment,
        ]);
      })
      .catch((error) => {
        toast.error(error[0], {
          position: "top-left",
        });
      });
  }

  updateSegmentHandler() {
    updateFiltersSegment({
      project: this.context.project,
      id: this.props.segment.id,
      filtersArg: this.state.selectedFilters,
      title: this.state.segmentName,
    })
      .then(() => {
        this.props.toggleCreateANewSegment();
        toast.success("Segment updated successfully", {
          position: "top-left",
        });
        this.context.updateAppliedFilters(this.state.selectedFilters);
        this.context.updateAppliedFiltersSegment({
          ...this.props.segment,
          title: this.state.segmentName,
          filters: this.state.selectedFilters,
        });
      })
      .catch((error) => {
        toast.error(error[0], {
          position: "top-left",
        });
      });
  }

  submitHandler(e) {
    e.preventDefault();
    if (this.props.segment) {
      this.updateSegmentHandler();
    } else {
      this.createNewSegmentHandler();
    }
  }

  render() {
    const { t } = this.props;
    return (
      <div className="create-segment">
        <section className="create-segment__main">
          <div className="create-segment__back">
            <BackArrowIcon />
            <button
              className="button-underlined black"
              onClick={this.props.toggleCreateANewSegment}
              type="button"
            >
              {t("back")}
            </button>
          </div>
          <form onSubmit={(e) => this.submitHandler(e)}>
            <fieldset className="advanced-filters__fieldset form-field">
              <legend>{t("segmentName")}</legend>
              <input
                type="text"
                id="segmentName"
                name="segmentName"
                placeholder={t("enterSegmentName")}
                value={this.state.segmentName}
                onChange={(e) => this.setState({ segmentName: e.target.value })}
              />
            </fieldset>
            <div className="advanced-filters">
              {this.context.loadingFilters ? (
                <SectionLoader />
              ) : (
                Object.keys(this.state.flattenedFilters)
                  // MOVE THE `SHIPPING_LOCATIONS_KEY` TO THE LAST INDEX
                  .sort((a, b) => {
                    return a === SHIPPING_LOCATIONS_KEY
                      ? 1
                      : b === SHIPPING_LOCATIONS_KEY
                      ? -1
                      : 0;
                  })
                  .map((key) => {
                    return key !== SHIPPING_LOCATIONS_KEY ? (
                      <fieldset
                        className="advanced-filters__fieldset"
                        key={key}
                      >
                        <legend>
                          {key === "state" ? t("orderState") : t(key)}
                        </legend>
                        <div className="advanced-filters__field">
                          <label htmlFor={`select-all-${key}`}>All</label>
                          <input
                            type="checkbox"
                            name={`all-${key}`}
                            id={`select-all-${key}`}
                            checked={this.getIsAllSelected(key)}
                            onChange={() => this.selectAll(key)}
                          />
                        </div>
                        {this.state.flattenedFilters[key].map((filter) => (
                          <div
                            className="advanced-filters__field"
                            key={`${key}-${filter}`}
                          >
                            <label htmlFor={`${key}-${filter}`}>{filter}</label>
                            <input
                              type="checkbox"
                              name={key}
                              id={`${key}-${filter}`}
                              value={filter}
                              checked={
                                !!this.state.selectedFilters[key]?.includes(
                                  filter
                                )
                              }
                              onChange={() => this.changeHandler(key, filter)}
                            />
                          </div>
                        ))}
                      </fieldset>
                    ) : (
                      <fieldset
                        className="advanced-filters__fieldset advanced-filters__location-field"
                        key={key}
                      >
                        <legend>{t(key)}</legend>
                        <Select
                          id={`${key}-input-container`}
                          inputId={`${key}-input`}
                          placeholder={t("searchLocationPlaceholder")}
                          theme={customTheme}
                          styles={customStyles}
                          components={{ DropdownIndicator, MenuList }}
                          options={mapFlattenedToMultiSelectValue(
                            this.state.flattenedFilters[key]
                          )}
                          value={mapFlattenedToMultiSelectValue(
                            this.state.selectedFilters[key]
                          )}
                          onChange={(val) => {
                            const value = Array.isArray(val) ? val : [val];
                            this.setState((prev) => ({
                              selectedFilters: {
                                ...prev.selectedFilters,
                                [key]: mapMultiSelectToFlattenedValue(value),
                              },
                            }));
                          }}
                          isMulti
                          isClearable
                        />
                      </fieldset>
                    );
                  })
              )}
              <div className="advanced-filters__cta">
                <button
                  className="button-underlined  black"
                  type="button"
                  onClick={() => this.setState({ selectedFilters: {} })}
                >
                  {t("reset", { ns: "settings" })}
                </button>
                <button className="button-primary small black" type="submit">
                  {this.props.segment ? t("updateSegment") : t("createSegment")}
                </button>
              </div>
            </div>
          </form>
        </section>
      </div>
    );
  }
}

CreateSegment.contextType = ANALYTICS_CONTEXT;
export default withTranslation(["filters"])(CreateSegment);
